"use client";

import { createContext, useContext, useState, ReactNode } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";


type ConfirmOptions = {
  title?: string;
  description: string;
  extraContent?: ReactNode;
  confirmText?: string;
  cancelText?: string;
  variant?: "default" | "destructive";
};

type Ctx = {
  confirm: (opts: ConfirmOptions) => Promise<boolean>;
  notify: (description: string, title?: string) => Promise<void>;
};

const DialogCtx = createContext<Ctx | null>(null);

export function DialogProvider({ children }: { children: ReactNode }) {
  
  const [open, setOpen] = useState(false);
  const [opts, setOpts] = useState<ConfirmOptions>({ description: "" });
  const [resolver, setResolver] = useState<((v: boolean) => void) | null>(null);
  const [isNotify, setIsNotify] = useState(false);

  const confirm = (o: ConfirmOptions) =>
    new Promise<boolean>((resolve) => {
      setOpts(o);
      setIsNotify(false);
      setResolver(() => resolve);
      setOpen(true);
    });

  const notify = (description: string, title?: string) =>
    new Promise<void>((resolve) => {
      setOpts({ description, title });
      setIsNotify(true);
      setResolver(() => () => resolve());
      setOpen(true);
    });

  const close = (result: boolean) => {
    setOpen(false);
    resolver?.(result);
    setResolver(null);
  };

  return (
    <DialogCtx.Provider value={{ confirm, notify }}>
      {children}
      <AlertDialog open={open} onOpenChange={(o) => !o && close(false)}>
        <AlertDialogContent
          className="border border-base"
          style={{ background: "rgb(var(--bg-panel))", color: "rgb(var(--foreground))" }}
        >
          <AlertDialogHeader>
            {opts.title && (
              <AlertDialogTitle style={{ color: "rgb(var(--foreground))" }}>
                {opts.title}
              </AlertDialogTitle>
            )}
            <AlertDialogDescription style={{ color: "rgb(var(--fg-secondary))" }}>
                {opts.description}
              </AlertDialogDescription>
              {opts.extraContent && (
                <div className="mt-2 text-xs">
                  {opts.extraContent}
                </div>
              )}
          </AlertDialogHeader>
          <AlertDialogFooter>
            {!isNotify && (
              <AlertDialogCancel
                onClick={() => close(false)}
                className="border-base"
                style={{ background: "rgb(var(--bg-card))", color: "rgb(var(--foreground))" }}
              >
                {opts.cancelText ?? "Cancel"}
              </AlertDialogCancel>
            )}
            <AlertDialogAction
              onClick={() => close(true)}
              className="text-white"
              style={{ background: opts.variant === "destructive" ? "rgb(239,68,68)" : "rgb(var(--accent))" }}
            >
              {opts.confirmText ?? "OK"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DialogCtx.Provider>
  );
}

export function useDialog() {
  const ctx = useContext(DialogCtx);
  if (!ctx) throw new Error("useDialog must be used inside DialogProvider");
  return ctx;
}